import axios, { AxiosRequestConfig, AxiosResponse, Method } from 'axios';

import { convertCamelCaseConvention } from './responseConvertors';
import { API_ROUTES } from '@/constants/apiRoutes';
import { RefreshTokenResponse } from '@/types/auth';
import { useAuthUser } from '@/hooks/stores/useAuthUser';
import { Address, PaginatedResponse } from '@/types/common';
import { convertToNumber } from './currency';
import { HealthMetrics } from './sentry/healthMetrics';

const baseUrl = import.meta.env.VITE_BASE_URL;

export const refreshToken = async () => {
  const accessToken = useAuthUser.getState().accessToken;
  if (accessToken?.expiration && Date.now() < accessToken?.expiration) {
    return accessToken;
  }

  const { data } = await api<RefreshTokenResponse>({
    url: API_ROUTES.REFRESH_TOKEN,
    method: 'post',
    isAuth: false,
    withCredentials: true,
  });

  const response = convertCamelCaseConvention(data) as RefreshTokenResponse;
  useAuthUser.setState({
    accessToken: { ...response, expiration: Date.now() + response.expiresIn },
  });

  return response;
};

export const logout = async () => {
  try {
    const response = await refreshToken();
    if (response.accessToken) {
      await api({
        url: API_ROUTES.LOGOUT,
        method: 'post',
        data: {
          access_token: response.accessToken,
        },
      });
    }
  } finally {
    HealthMetrics.clearUserContext();
    window.location.href = '/login';
  }
};

export const authApiHeader = async () => {
  try {
    const session = await refreshToken();

    if (!session || !session.idToken) {
      await logout();
      return;
    }

    return { Authorization: `Bearer ${session.idToken}` };
  } catch {
    logout();
  }
};

type ApiRequest = {
  url: string;
  method: Method;
  version?: number;
  data?: any;
  isAuth?: boolean;
  withCredentials?: boolean;
  idempotencyKey?: string;
};

export async function api<T>({
  url,
  method,
  version = 1,
  data,
  isAuth = true,
  withCredentials = false,
  idempotencyKey,
}: ApiRequest): Promise<AxiosResponse<T>> {
  let baseRequest: AxiosRequestConfig = {
    baseURL: `${baseUrl}v${version}/`,
    method,
    data,
    withCredentials,
  };

  if (isAuth) {
    const headers = await authApiHeader();
    baseRequest = {
      headers,
      ...baseRequest,
    };
  }

  if (idempotencyKey) {
    baseRequest.headers = {
      ...baseRequest.headers,
      ...{
        'SV1-Idempotency-Key': idempotencyKey,
      },
    };
  }

  try {
    const response = await axios(url, baseRequest);

    return {
      ...response,
      data: convertCamelCaseConvention(response.data) as T,
    };
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const fullUrl = `${baseRequest.baseURL}${url}`;
      HealthMetrics.trackAPIError(
        error as Error,
        fullUrl,
        error.response?.status,
      );
    }
    throw error;
  }
}

export function getNextPageParam(response: PaginatedResponse<any>) {
  const onLastPage =
    response.lastPage === -1 || response.currentPage === response.lastPage;

  return onLastPage ? undefined : response.currentPage + 1;
}

export function formatAddressForPost(address: Address) {
  return {
    line1: address.line1,
    line2: address.line2,
    postal_code: address.postalCode,
    state: address.state,
    city: address.city,
  };
}

export function formatCurrencyForPost(value: string | number) {
  return { currency: 'usd', value: convertToNumber(`${value}`) };
}
